/**
 * @fileoverview Defines the `Folder` and `FileData` types and provides functions for recursively reading and processing folder structures, including `getFolder`, `walkFolder`, and `walkFolderAsync`. The `AwolfIgnorer` class is used to filter files during directory traversal.
 */

import { serializeForLlm } from '@iterator/shared-common/src/serialize_for_llm'
import { debug } from '@iterator/shared-node/util/logging'
import { baseOfSparsePath, SparsePath } from '@iterator/shared-node/util/sparse_path'
import { tokenCount } from '@iterator/shared-node/util/tokens'
import {
  walkFolder as doWalkFolder,
  walkFolderAsync as doWalkFolderAsync,
} from '@iterator/shared-node/util/walk_folder'
import * as fs from 'fs'
import * as pathlib from 'path'
import { AwolfIgnorer } from './awolf_ignore'

export type FileData = {
  path: string
  contents: string
  tokenCount: number
}

export type Folder = {
  path: string
  files: FileData[]
  folders: Folder[]
}

// Tokenization is slow, so only do it in verbose mode when debugging.
export function getTokenCount(contents: string, skip: boolean = false): number {
  if (!skip) {
    return tokenCount(contents)
  }
  return 0
}

/**
 * Recursively gets file metadata and contents for a given file path.
 *
 * @param folderPath - Path to the folder to read
 * @param skipTokenCount - If true, skips calculating token count for performance. Default is false.
 * @returns [Folder] object containing path, files and subfolders
 */
export function getFolder(folderPath: SparsePath, skipTokenCount: boolean = false): Folder {
  const folderPathToFiles: Record<string, FileData[]> = {}
  const folderPathToSubFolderPaths: Record<string, string[]> = {}
  const addToMapOfLists = <T>(map: Record<string, T[]>, key: string, value: T) => {
    if (!map[key]) {
      map[key] = []
    }
    map[key].push(value)
  }

  walkFolder(folderPath, (filePath, type) => {
    const parentPath = pathlib.dirname(filePath)
    if (type === 'file') {
      addToMapOfLists(folderPathToFiles, parentPath, getFileData(filePath, skipTokenCount))
    } else if (type === 'folder') {
      addToMapOfLists(folderPathToSubFolderPaths, parentPath, filePath)
    }
  })

  const buildFolderObject = (folderPath: string): Folder => {
    return {
      path: folderPath,
      files: folderPathToFiles[folderPath] || [],
      folders: folderPathToSubFolderPaths[folderPath]?.map(buildFolderObject) || [],
    }
  }

  return buildFolderObject(baseOfSparsePath(folderPath))
}

/**
 * Walks a folder and its subfolders, calling a handler function for each file and folder. Allows
 * for custom ignorers to skip files. By default will respect .awignore and .gitignore as well as
 * ignore .awolf directories and non-text files.
 *
 * @param folderPath - Path to the root folder to walk
 * @param handleFile - Function to handle each file or folder
 */
export async function walkFolder(
  folderPath: SparsePath,
  handleFile: (path: string, type: 'file' | 'folder') => void,
) {
  return doWalkFolder(folderPath, handleFile, new AwolfIgnorer(folderPath))
}

/**
 * Walks a folder and its subfolders, calling a handler function for each file and folder. Allows
 * for custom ignorers to skip files. By default will respect .awignore and .gitignore as well as
 * ignore .awolf directories and non-text files.
 *
 * @param folderPath - Path to the root folder to walk or SparseDirectory object
 * @param handleFile - Function to handle each file or folder
 */
export async function walkFolderAsync(
  folderPath: string | SparsePath,
  handleFile: (path: string, type: 'file' | 'folder') => Promise<void>,
) {
  return doWalkFolderAsync(folderPath, handleFile, new AwolfIgnorer(folderPath))
}

export function allFiles(folder: Folder): FileData[] {
  return [...folder.files, ...folder.folders.flatMap((f) => allFiles(f))]
}

export function serializeFolder(folder: Folder): string {
  let result = ''
  const toSerialize = [folder]
  const filesInOrder = []

  while (toSerialize.length > 0) {
    const currentFolder = toSerialize.pop()
    if (!currentFolder) {
      continue
    }

    result += currentFolder.path + '\n'
    for (const file of currentFolder.files) {
      filesInOrder.push(file)
      result += file.path + '\n'
    }
    for (const subFolder of currentFolder.folders) {
      toSerialize.push(subFolder)
    }
  }

  for (let i = 0; i < filesInOrder.length; i++) {
    const file = filesInOrder[i]
    if (!file) {
      continue
    }

    result += '\n' + file.path + '\n' + file.contents
    if (i < filesInOrder.length - 1) {
      result += '\n'
    }
  }

  return result
}

export function logFolderAndFiles(folder: Folder) {
  debug('\n' + folder.path)
  let totalTokenCount = 0
  for (const file of folder.files) {
    debug('  ' + file.path + ' ' + file.tokenCount)
    totalTokenCount += file.tokenCount
  }
  debug('  Total token count: ' + totalTokenCount)
  for (const subFolder of folder.folders) {
    logFolderAndFiles(subFolder)
  }
}

export function serializeFolderStructure(folder: Folder): string {
  let result = ''
  const toSerialize = [folder]
  while (toSerialize.length > 0) {
    const currentFolder = toSerialize.pop()
    if (!currentFolder) {
      continue
    }

    result += currentFolder.path + '\n'
    for (const file of currentFolder.files) {
      result += file.path + '\n'
    }
    for (const subFolder of currentFolder.folders) {
      toSerialize.push(subFolder)
    }
  }
  return result
}

export function serializeFilesToTexts(folder: Folder): Array<string> {
  const messages = []
  const toSerialize = [folder]
  while (toSerialize.length > 0) {
    const currentFolder = toSerialize.pop()
    if (!currentFolder) {
      continue
    }

    for (const file of currentFolder.files) {
      messages.push(serializeForLlm.file(file))
    }
    for (const subFolder of currentFolder.folders) {
      toSerialize.push(subFolder)
    }
  }

  return messages
}

export function getFileHierarchy(path: SparsePath): string {
  const resultStrings: string[] = []
  walkFolder(path, (filePath, type) => {
    resultStrings.push(
      (type === 'folder' ? 'folder://' : 'file://') +
        pathlib.relative(baseOfSparsePath(path), filePath),
    )
  })
  return resultStrings.join('\n')
}

export function getFileData(filePath: string, skipTokenCount: boolean = false): FileData {
  return {
    path: filePath,
    contents: fs.readFileSync(filePath, 'utf8'),
    tokenCount: getTokenCount(fs.readFileSync(filePath, 'utf8'), skipTokenCount),
  }
}
